import React, { ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { IconButtonProps } from '@osmosis/tailwind-ui/components/IconButton';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { H3 } from '@osmosis/tailwind-ui/components/Text';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { track } from '../../lib/util/tracking';

type Rating = 'low' | 'medium' | 'high';

export enum RatingEmoji {
  low = '😰',
  medium = '😐',
  high = '😁',
}

const EmojiButton = ({
  currentRating,
  rating,
  formattedMessage,
  ...rest
}: IconButtonProps & {
  rating: Rating;
  currentRating: Rating | undefined;
  formattedMessage: ReactNode;
}) => {
  return (
    <>
      <IconButton
        variant="outline"
        color="white"
        data-testid={`rating-button-${rating}`}
        data-tooltip-id={`rating-${rating}`}
        icon={<Box>{RatingEmoji[rating]}</Box>}
        disabled={!!currentRating && currentRating !== rating}
        className="text-base sm:text-2xl"
        {...rest}
      />

      <Tooltip id={`rating-${rating}`}>
        <P className="text0">
          <FormattedMessage
            id="learn.video.rating.prompt"
            defaultMessage="Rate your understanding:"
          />
        </P>
        <P className="text0 text-center">{formattedMessage}</P>
      </Tooltip>
    </>
  );
};

/** Video rating with emojis. */
const VideoRating = () => {
  const [rating, setRating] = useState<Rating>();

  const handleRate = (newRating: Rating) => {
    if (!rating) {
      track(
        'feature_engagement',
        {
          feature: 'video_confidence',
          action: newRating,
        },
        ['keen'],
      );

      setRating(newRating);
    }
  };

  return (
    <Box>
      <Flex className="items-center gap-3">
        <Box className="w-[350px]">
          <H3 className="text0 hidden text-right sm:block">
            {rating ? (
              <FormattedMessage
                defaultMessage="Thanks for the feedback!"
                id="learn.video.overlay.thanks_text"
              />
            ) : (
              <FormattedMessage
                id="learn.video.overlay.understand_text"
                defaultMessage="How well did you understand this video?"
              />
            )}
          </H3>
        </Box>

        <Flex className="gap-2">
          <EmojiButton
            data-testid="low-rating-button"
            currentRating={rating}
            rating="low"
            onClick={() => handleRate('low')}
            formattedMessage={
              <FormattedMessage
                id="learn.video.rating.low"
                defaultMessage="Poor"
              />
            }
          />

          <EmojiButton
            data-testid="medium-rating-button"
            currentRating={rating}
            rating="medium"
            onClick={() => handleRate('medium')}
            formattedMessage={
              <FormattedMessage
                id="learn.video.rating.medium"
                defaultMessage="Average"
              />
            }
          />

          <EmojiButton
            data-testid="high-rating-button"
            currentRating={rating}
            rating="high"
            onClick={() => handleRate('high')}
            formattedMessage={
              <FormattedMessage
                id="learn.video.rating.high"
                defaultMessage="Great!"
              />
            }
          />
        </Flex>
      </Flex>
    </Box>
  );
};

export default VideoRating;
